import type { ForecastMarketPair } from '@/features/bidding/types/forecastPricing'
import type { CascadeMarketId } from '@/features/bidding/types/market'

/**
 * Get market program names for both directions of a market pair.
 */
export function getMarketProgramNames(market: ForecastMarketPair): {
  up: string
  down: string
  displayName: string
} {
  switch (market) {
    case 'mfrr':
      return { up: 'mFRR UP', down: 'mFRR DOWN', displayName: 'mFRR' }
    case 'fcr-d':
      return { up: 'FCR-D UP', down: 'FCR-D DOWN', displayName: 'FCR-D' }
    case 'fcr-n':
      return { up: 'FCR-N', down: 'FCR-N', displayName: 'FCR-N' }
    case 'opportunity-cost':
      return { up: 'mFRR UP', down: 'mFRR DOWN', displayName: 'Opp. Cost' }
    default:
      return { up: 'mFRR UP', down: 'mFRR DOWN', displayName: 'mFRR' }
  }
}

/**
 * Get market program name for a specific direction of a market pair.
 */
export function getMarketProgramNameForDirection(marketPair: ForecastMarketPair, direction: 'up' | 'down'): string {
  const names = getMarketProgramNames(marketPair)
  return names[direction]
}

/**
 * Check if a market pair is symmetric (no UP/DOWN distinction).
 * Symmetric markets like FCR-N have the same program name for both directions.
 */
export function isSymmetricMarket(market: ForecastMarketPair): boolean {
  const names = getMarketProgramNames(market)
  return names.up === names.down
}

/**
 * Convert a CascadeMarketId to a ForecastMarketPair for use with forecast data.
 *
 * The cascade bidding UI uses CascadeMarketId which includes 'da-wholesale',
 * but forecast data uses ForecastMarketPair which doesn't include wholesale markets.
 * For da-wholesale, we use mFRR forecast data as a proxy since wholesale prices
 * are closely correlated with balancing market prices.
 */
export function cascadeMarketToForecastMarket(market: CascadeMarketId): ForecastMarketPair {
  if (market === 'da-wholesale') {
    return 'mfrr'
  }
  return market
}
